import axios from 'axios';
import { motion } from 'framer-motion';
import {
    ArrowRight,
    Award,
    Bell,
    Bookmark,
    Briefcase,
    Calendar,
    CheckCircle,
    Clock,
    Eye,
    FileText,
    Heart,
    LogOut,
    MapPin,
    MessageSquare,
    MoreVertical,
    Search,
    Settings,
    Star,
    TrendingUp,
    User,
    X,
    Zap
} from 'lucide-react';
import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ResumeParsingStatus from './ResumeParsingStatus';

const Dashboard = () => {
  const { user, isAuthenticated, token, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [recentJobs, setRecentJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({
    totalApplications: 0,
    underReview: 0,
    interviews: 0,
    offers: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeQuickAction, setActiveQuickAction] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📤 USER DASHBOARD - Fetching dashboard data with token:', !!token);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/dashboard`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('📥 USER DASHBOARD - Dashboard response:', response.data);

      if (response.data.success) {
        const { stats: dashboardStats, recentJobs: dashboardJobs, recentApplications } = response.data.data;
        
        setStats(dashboardStats);
        setRecentJobs(dashboardJobs);
        setApplications(recentApplications);

        console.log('✅ USER DASHBOARD - Data set successfully:', {
          stats: dashboardStats,
          recentJobsCount: dashboardJobs.length,
          applicationsCount: recentApplications.length
        });
      }
    } catch (error) {
      console.error('❌ USER DASHBOARD - Error fetching dashboard data:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Set fallback data on error
      setStats({
        totalApplications: 0,
        underReview: 0,
        interviews: 0,
        offers: 0
      });
      setRecentJobs([]);
      setApplications([]);
      
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Wait until auth context finishes loading token/user
    if (authLoading) return;

    if (!isAuthenticated || !token) {
      navigate('/login');
      return;
    }

    fetchDashboardData();
  }, [isAuthenticated, authLoading, navigate, token, fetchDashboardData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Under Review': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Interview Scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Shortlisted': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Selected': return 'bg-green-100 text-green-800 border-green-200';
      case 'Rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Under Review': return <Clock className="w-4 h-4" />;
      case 'Interview Scheduled': return <Calendar className="w-4 h-4" />;
      case 'Shortlisted': return <Star className="w-4 h-4" />;
      case 'Selected': return <CheckCircle className="w-4 h-4" />;
      case 'Rejected': return <X className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const handleLogout = () => {
    logout();
  };

  const quickActions = [
    {
      id: 'browse-jobs',
      title: 'Browse Jobs',
      description: 'Discover new opportunities',
      icon: Search,
      color: 'from-blue-500 to-cyan-500',
      link: '/jobs'
    },
    {
      id: 'update-profile',
      title: 'Update Profile',
      description: 'Keep your profile current',
      icon: User,
      color: 'from-emerald-500 to-green-500',
      link: '/profile'
    },
    {
      id: 'upload-resume',
      title: 'Upload Resume',
      description: 'Add or update your resume',
      icon: FileText,
      color: 'from-purple-500 to-violet-500',
      link: '/profile'
    },
    {
      id: 'job-alerts',
      title: 'Job Alerts',
      description: 'Set up custom alerts',
      icon: Bell,
      color: 'from-amber-500 to-orange-500',
      link: '/settings'
    }
  ];

  const upcomingEvents = [
    {
      id: 1,
      title: 'Technical Interview',
      company: 'TechCorp Inc.',
      time: 'Today, 2:00 PM',
      type: 'interview',
      color: 'bg-blue-500'
    },
    {
      id: 2,
      title: 'Follow-up Call',
      company: 'StartupHub',
      time: 'Tomorrow, 10:30 AM',
      type: 'call',
      color: 'bg-emerald-500'
    },
    {
      id: 3,
      title: 'Final Round',
      company: 'InnovateX',
      time: 'Thursday, 3:00 PM',
      type: 'interview',
      color: 'bg-purple-500'
    }
  ];

  const insights = [
    {
      title: 'Profile Views',
      value: '24',
      change: '+12%',
      trend: 'up',
      icon: Eye,
      description: 'This week'
    },
    {
      title: 'Application Rate',
      value: '85%',
      change: '+5%',
      trend: 'up',
      icon: TrendingUp,
      description: 'Success rate'
    },
    {
      title: 'Saved Jobs',
      value: '12',
      change: '+3',
      trend: 'up',
      icon: Bookmark,
      description: 'This month'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="nav-luxury sticky top-0 z-50">
        <div className="container-luxury">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-3 group">
                <div className="w-10 h-10 gradient-bg-luxury rounded-2xl flex items-center justify-center shadow-luxury group-hover:scale-105 transition-transform">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-display font-bold gradient-text">JobZentro</h1>
                  <p className="text-xs text-slate-500 font-medium">Job Portal</p>
                </div>
              </Link>
              <nav className="hidden lg:flex space-x-8 ml-8">
                <Link to="/dashboard" className="nav-link active">Dashboard</Link>
                <Link to="/jobs" className="nav-link">Browse Jobs</Link>
                <Link to="/applications" className="nav-link">Applications</Link>
                <Link to="/profile" className="nav-link">Profile</Link>
                <Link to="/about" className="nav-link">About</Link>
                <Link to="/contact" className="nav-link">Contact</Link>
              </nav>
            </div>
            
            <div className="flex items-center space-x-4">
              <button className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 transition-colors relative">
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              </button>
              <button className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <Link to="/profile" className="flex items-center space-x-3 p-2 rounded-xl hover:bg-slate-100 transition-colors">
                <img
                  src={user?.profilePicture || `https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face`}
                  alt="Profile"
                  className="w-10 h-10 rounded-full object-cover shadow-soft"
                />
                <div className="hidden md:block text-left">
                  <p className="text-sm font-heading font-semibold text-slate-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-slate-500">Job Seeker</p>
                </div>
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors p-2 rounded-xl hover:bg-slate-100"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-luxury py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-display font-bold text-slate-900 mb-2">
                Welcome back, {user?.firstName}! 👋
              </h1>
              <p className="text-xl text-slate-600">
                Ready to take the next step in your career journey?
              </p>
            </div>
            <div className="hidden lg:block">
              <div className="text-right">
                <p className="text-sm text-slate-500">Last login</p>
                <p className="text-sm font-medium text-slate-700">Today, 9:30 AM</p>
              </div>
            </div>
          </div>

          {/* Quick Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Applications', value: stats.totalApplications, icon: FileText, color: 'from-blue-500 to-cyan-500' },
              { label: 'Under Review', value: stats.underReview, icon: Clock, color: 'from-emerald-500 to-green-500' },
              { label: 'Interviews', value: stats.interviews, icon: Calendar, color: 'from-purple-500 to-violet-500' },
              { label: 'Offers', value: stats.offers, icon: Award, color: 'from-amber-500 to-orange-500' }
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="card-luxury p-6 text-center group hover:scale-105 transition-all duration-300"
                >
                  <div className={`w-12 h-12 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl font-bold font-heading mb-1 gradient-text-primary">{stat.value}</div>
                  <div className="text-sm text-slate-600 font-medium">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="card-luxury p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-display font-bold text-slate-900">Quick Actions</h2>
                <button className="text-slate-400 hover:text-slate-600 transition-colors">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {quickActions.map((action, index) => {
                  const Icon = action.icon;
                  const isActive = activeQuickAction === action.id;
                  
                  return (
                    <motion.div
                      key={action.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Link
                        to={action.link}
                        className={`block p-6 rounded-2xl border-2 transition-all duration-300 group ${
                          isActive 
                            ? 'border-blue-300 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300 bg-white hover:shadow-lg'
                        }`}
                        onClick={() => setActiveQuickAction(action.id)}
                      >
                        <div className="flex items-start space-x-4">
                          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${action.color} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow flex-shrink-0`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-semibold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                              {action.title}
                            </h3>
                            <p className="text-sm text-slate-600">{action.description}</p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Recent Applications */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card-luxury p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-display font-bold text-slate-900">Recent Applications</h2>
                <Link to="/applications" className="text-blue-600 hover:text-blue-700 font-medium transition-colors flex items-center space-x-1">
                  <span>View All</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="space-y-4">
                {applications.length > 0 ? applications.slice(0, 4).map((application, index) => (
                  <motion.div
                    key={application.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-center justify-between p-6 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors group"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <Briefcase className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                          {application.jobTitle}
                        </h3>
                        <p className="text-sm text-slate-600">{application.company}</p>
                        <p className="text-xs text-slate-500">Applied {application.appliedDate}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(application.status)}`}>
                        {getStatusIcon(application.status)}
                        <span className="ml-1">{application.status}</span>
                      </span>
                      {application.nextStep && (
                        <p className="text-xs text-slate-500 mt-1">{application.nextStep}</p>
                      )}
                    </div>
                  </motion.div>
                )) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No applications yet</h3>
                    <p className="text-slate-600 mb-4">Start applying to jobs to see them here</p>
                    <Link to="/jobs" className="btn-primary inline-flex">
                      Browse Jobs
                      <Search className="w-4 h-4 ml-2" />
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Career Insights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="card-luxury p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-display font-bold text-slate-900">Career Insights</h2>
                <Link to="/insights" className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
                  View Details
                </Link>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {insights.map((insight, index) => {
                  const Icon = insight.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="p-6 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <span className={`text-sm font-medium ${
                          insight.trend === 'up' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {insight.change}
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-slate-900 mb-1">{insight.value}</div>
                      <div className="text-sm font-medium text-slate-700 mb-1">{insight.title}</div>
                      <div className="text-xs text-slate-500">{insight.description}</div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Profile Completion */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card-luxury p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-display font-bold text-slate-900">Profile Strength</h2>
                <span className="text-sm font-medium gradient-text-primary">{user?.profileCompleteness || 75}%</span>
              </div>
              
              <div className="w-full bg-slate-200 rounded-full h-3 mb-6">
                <motion.div
                  className="h-3 gradient-bg-primary rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${user?.profileCompleteness || 75}%` }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </div>
              
              <div className="space-y-3 mb-6">
                {[
                  { label: 'Basic Info', completed: true },
                  { label: 'Experience', completed: true },
                  { label: 'Skills', completed: true },
                  { label: 'Resume', completed: false }
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    {item.completed ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <div className="w-4 h-4 border-2 border-slate-300 rounded-full" />
                    )}
                    <span className={`text-sm ${item.completed ? 'text-slate-700' : 'text-slate-500'}`}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
              
              <Link to="/profile" className="btn-primary w-full">
                Complete Profile
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>

            {/* Upcoming Events */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="card-luxury p-8"
            >
              <h2 className="text-xl font-display font-bold text-slate-900 mb-6">Upcoming Events</h2>
              
              <div className="space-y-4">
                {upcomingEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-start space-x-4 p-4 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors"
                  >
                    <div className={`w-3 h-3 ${event.color} rounded-full mt-2 flex-shrink-0`}></div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-semibold text-slate-900 text-sm">{event.title}</h3>
                      <p className="text-sm text-slate-600">{event.company}</p>
                      <p className="text-xs text-slate-500 flex items-center mt-1">
                        <Clock className="w-3 h-3 mr-1" />
                        {event.time}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <Link to="/calendar" className="btn-outline w-full mt-6">
                <Calendar className="w-4 h-4 mr-2" />
                View Calendar
              </Link>
            </motion.div>

            {/* Recommended Jobs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="card-luxury p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-display font-bold text-slate-900">Recommended Jobs</h2>
                <Link to="/jobs" className="text-blue-600 hover:text-blue-700 font-medium transition-colors flex items-center space-x-1">
                  <span>View All</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="space-y-4">
                {recentJobs.length > 0 ? recentJobs.slice(0, 3).map((job, index) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="border border-slate-200 rounded-2xl p-4 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                    onClick={() => navigate(`/job/${job.id}`)}
                  >
                    <div className="flex items-start space-x-3">
                      <img
                        src={job.logo}
                        alt={job.company}
                        className="w-12 h-12 rounded-xl object-cover shadow-soft"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-semibold text-slate-900 text-sm group-hover:text-blue-600 transition-colors truncate">
                          {job.title}
                        </h3>
                        <p className="text-sm text-slate-600 truncate">{job.company}</p>
                        <div className="flex items-center space-x-2 mt-2">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          <span className="text-xs text-slate-500 truncate">{job.location}</span>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-sm font-medium gradient-text-primary">{job.salary}</span>
                          <button className="p-1 text-slate-400 hover:text-red-500 transition-colors">
                            <Heart className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Briefcase className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-sm text-slate-500 mb-3">No recommendations yet</p>
                    <Link to="/jobs" className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors">
                      Browse all jobs
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>

            {/* AI Career Assistant */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="card-luxury p-8 gradient-bg-luxury text-white relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
              <div className="relative z-10">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-lg font-display font-bold">AI Career Assistant</h2>
                </div>
                <p className="text-white/90 text-sm mb-6 leading-relaxed">
                  Get personalized career advice and job recommendations powered by AI.
                </p>
                <button className="btn-glass w-full justify-center">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Chat with AI
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Resume Parsing Status */}
      <ResumeParsingStatus />
    </div>
  );
};

export default Dashboard; 